/**
 * Visit "Mark Done" Drawer (Phase 7.D)
 *
 * ESM replacement for the inline ``<script>`` that used to live in
 * ``templates/office/visits/partials/_mark_done_drawer.html`` (369 LOC,
 * the heaviest of the 4 visit-action partials).
 *
 * Bespoke widgets handled here:
 *
 *   1. Live duration display. While the user fills in actual start
 *      and end (date + time), we recompute the elapsed minutes on
 *      every change/changeDate/input/blur and either:
 *        - show "Actual: 1h 30m (90 min)" under the inputs, OR
 *        - flag "End time must be after start time" with a red border
 *          on all four inputs, OR
 *        - clear both (don't nag while the user is still filling).
 *      ``updateDurationDisplay()`` is the single source of truth for
 *      time-related validation; the submit-time validator only checks
 *      that the live state is healthy + that all fields are filled.
 *
 *   2. Breaks management. Local in-memory list (``mdBreaks``), with
 *      a small inline form to add a break (type / duration / start /
 *      end), mutual-exclusion between "duration" and "start+end",
 *      a pretty rendered list with per-row remove buttons, and a
 *      hidden-input mirror so the breaks ride along with the form
 *      submit (4 parallel arrays: break_types, break_durations,
 *      break_starts, break_ends — same wire format as the legacy
 *      inline implementation).
 *
 *   3. Cleaner-picker selection listener clears the picker error as
 *      soon as the user picks a cleaner.
 *
 *   4. Submit-time validators (clearAllErrors, validateTimes,
 *      validateCleaners), wired into ``wireDrawerActionForm`` with
 *      ``expectJson: true`` (mark-done returns
 *      ``{success: true|false, error?, message?}``).
 *
 *   5. Drawer-scoped Flowbite boot (Datepicker on the actual_start /
 *      actual_end inputs).
 *
 *   6. Delegated click handler for the in-form action buttons
 *      (toggle-break-form / add-break / remove-break) — replaces
 *      the inline ``onclick="…"`` attributes that lived on the
 *      buttons before Phase 7.
 *
 * Translation strings flow through ``json_script`` because the partial
 * has more than a dozen strings and ``data-*`` attributes would
 * become unwieldy.
 */

const BADGE_CLASSES = {
    rest: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    lunch: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    other: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function readJsonScript(id) {
    const node = document.getElementById(id);
    if (!node) return {};
    try {
        return JSON.parse(node.textContent || '{}');
    } catch (err) {
        console.warn(`readJsonScript: failed to parse #${id}`, err);
        return {};
    }
}

function initVisitMarkDoneDrawer(rootEl) {
    if (!rootEl) return;
    const T = readJsonScript('drawerMarkDoneI18n');

    const titleEl = document.getElementById('drawerLabel');
    if (titleEl && T.markDone) titleEl.textContent = T.markDone;

    const timeErrorEl = rootEl.querySelector('#drawerMarkDoneTimeError');
    const cleanerErrorEl = rootEl.querySelector('#drawerMarkDoneCleanerError');
    const durationDisplay = rootEl.querySelector('#drawerActualDurationDisplay');

    const startDateInput = rootEl.querySelector('#drawer_actual_start_date');
    const startTimeInput = rootEl.querySelector('#drawer_actual_start_time');
    const endDateInput = rootEl.querySelector('#drawer_actual_end_date');
    const endTimeInput = rootEl.querySelector('#drawer_actual_end_time');
    const timeInputs = [startDateInput, startTimeInput, endDateInput, endTimeInput];

    // Per-agent-hour billing preview (office-only hourly visits). Enabled via
    // data-attrs on the drawer root; mobile-tracked visits are excluded since
    // their billing sums each agent's real punch-in, not this single window.
    const billingPreviewEl = rootEl.querySelector('#drawerBillingPreview');
    const billingPreviewEnabled = rootEl.dataset.billingPreview === '1';
    const billingHeadcount = parseInt(rootEl.dataset.headcount || '1', 10) || 1;
    const billingRate = parseFloat(rootEl.dataset.hourlyRate || '0') || 0;

    function fmtEur(n) {
        return `${Number(n).toFixed(2).replace('.', ',')} €`;
    }

    function clearBillingPreview() {
        if (billingPreviewEl) billingPreviewEl.textContent = '';
    }

    function renderBillingPreview(diffMins) {
        if (!billingPreviewEl || !billingPreviewEnabled) return;
        const total = (diffMins / 60) * billingHeadcount * billingRate;
        const agentsWord = billingHeadcount > 1 ? (T.agentPlural || '') : (T.agentSingular || '');
        const durLabel = `${Math.floor(diffMins / 60)}h${String(diffMins % 60).padStart(2, '0')}`;
        billingPreviewEl.innerHTML =
            `${T.billedLabel || ''} ${billingHeadcount} ${agentsWord} × ${durLabel} × ${fmtEur(billingRate)}/h = <strong>${fmtEur(total)}</strong>`;
    }

    function updateDurationDisplay() {
        const startDate = startDateInput?.value;
        const startTime = startTimeInput?.value;
        const endDate = endDateInput?.value;
        const endTime = endTimeInput?.value;
        const allFilled = !!(startDate && startTime && endDate && endTime);

        if (!allFilled) {
            if (durationDisplay) durationDisplay.textContent = '';
            clearBillingPreview();
            if (timeErrorEl) timeErrorEl.classList.add('hidden');
            timeInputs.forEach((input) => input?.classList.remove('border-red-500'));
            return;
        }

        const startParts = startDate.split('/');
        const endParts = endDate.split('/');
        if (startParts.length !== 3 || endParts.length !== 3) {
            if (durationDisplay) durationDisplay.textContent = '';
            clearBillingPreview();
            return;
        }

        const startDateTime = new Date(
            parseInt(startParts[2], 10),
            parseInt(startParts[1], 10) - 1,
            parseInt(startParts[0], 10),
            parseInt(startTime.split(':')[0], 10),
            parseInt(startTime.split(':')[1], 10),
        );
        const endDateTime = new Date(
            parseInt(endParts[2], 10),
            parseInt(endParts[1], 10) - 1,
            parseInt(endParts[0], 10),
            parseInt(endTime.split(':')[0], 10),
            parseInt(endTime.split(':')[1], 10),
        );

        const diffMins = Math.round((endDateTime - startDateTime) / 60000);

        if (diffMins <= 0) {
            if (durationDisplay) durationDisplay.textContent = '';
            clearBillingPreview();
            if (timeErrorEl) {
                timeErrorEl.textContent = T.endAfterStart || '';
                timeErrorEl.classList.remove('hidden');
            }
            timeInputs.forEach((input) => input?.classList.add('border-red-500'));
            return;
        }

        if (timeErrorEl) timeErrorEl.classList.add('hidden');
        timeInputs.forEach((input) => input?.classList.remove('border-red-500'));
        if (durationDisplay) {
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            const durationStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
            durationDisplay.innerHTML = `${T.actual || ''} <strong>${durationStr}</strong> (${diffMins} min)`;
        }
        renderBillingPreview(diffMins);
    }

    timeInputs.forEach((input) => {
        if (!input) return;
        ['change', 'changeDate', 'input', 'blur'].forEach((evt) => {
            input.addEventListener(evt, updateDurationDisplay);
        });
    });
    updateDurationDisplay();

    const markDonePicker = rootEl.querySelector('#drawer_mark_done_picker');
    if (markDonePicker) {
        markDonePicker.addEventListener('cleanerSelectionChanged', (e) => {
            if (e.detail && e.detail.selectedCleaners && e.detail.selectedCleaners.length > 0) {
                if (cleanerErrorEl) cleanerErrorEl.classList.add('hidden');
                const wrapper = markDonePicker.querySelector('.flex.flex-wrap.items-center');
                if (wrapper) wrapper.classList.remove('border-red-500');
            }
        });
    }

    function validateTimes() {
        const startDate = startDateInput?.value;
        const startTime = startTimeInput?.value;
        const endDate = endDateInput?.value;
        const endTime = endTimeInput?.value;

        if (!startDate || !startTime || !endDate || !endTime) {
            if (timeErrorEl) {
                timeErrorEl.textContent = T.fillTimes || '';
                timeErrorEl.classList.remove('hidden');
            }
            return false;
        }
        return timeErrorEl ? timeErrorEl.classList.contains('hidden') : true;
    }

    function validateCleaners() {
        const picker = rootEl.querySelector('#drawer_mark_done_picker');
        // Slice F.15 — externally-handled visits render an info card
        // instead of the picker (template branches on
        // ``is_externally_handled``). When the picker isn't in the DOM,
        // there's no cleaner to validate: skip the check entirely.
        // The POST handler also drops any incoming ``completed_by`` for
        // these visits, so this is purely a UX-side guard.
        if (!picker) return true;
        const hiddenField = picker.querySelector('.hidden-select');
        let hasCleaners = false;
        if (hiddenField) {
            if (hiddenField.tagName === 'SELECT') {
                const selectedOptions = Array.from(hiddenField.selectedOptions);
                hasCleaners = selectedOptions.length > 0 && selectedOptions.some((opt) => opt.value);
            } else {
                hasCleaners = !!hiddenField.value;
            }
        }
        if (hasCleaners) return true;
        if (cleanerErrorEl) {
            cleanerErrorEl.textContent = T.selectCleaner || '';
            cleanerErrorEl.classList.remove('hidden');
        }
        const wrapper = picker.querySelector('.flex.flex-wrap.items-center');
        if (wrapper) wrapper.classList.add('border-red-500');
        return false;
    }

    function clearAllErrors() {
        if (timeErrorEl) timeErrorEl.classList.add('hidden');
        if (cleanerErrorEl) cleanerErrorEl.classList.add('hidden');
        const picker = rootEl.querySelector('#drawer_mark_done_picker');
        if (picker) {
            const wrapper = picker.querySelector('.flex.flex-wrap.items-center');
            if (wrapper) wrapper.classList.remove('border-red-500');
        }
        return true;
    }

    if (typeof window.wireDrawerActionForm === 'function') {
        window.wireDrawerActionForm({
            formId: 'drawerMarkDoneForm',
            submitBtnId: 'drawerMarkDoneSubmit',
            validators: [clearAllErrors, validateTimes, validateCleaners],
            expectJson: true,
            successMessage: T.visitMarkedDone || '',
            errorMessage: T.errorMarkDone || '',
            errorContainerId: 'drawerMarkDoneTimeError',
        });
    }

    setupBreaksManagement(rootEl, T);

    if (typeof window.initFlowbite === 'function') {
        setTimeout(window.initFlowbite, 100);
    }
}

function setupBreaksManagement(rootEl, T) {
    const breaks = [];
    const breaksList = rootEl.querySelector('#markDoneBreaksList');
    const breaksHidden = rootEl.querySelector('#markDoneBreaksHidden');
    const breakForm = rootEl.querySelector('#markDoneBreakFields');
    const breakToggle = rootEl.querySelector('#markDoneAddBreakToggle');
    const breakError = rootEl.querySelector('#mdBreakError');

    if (!breaksList || !breaksHidden || !breakForm || !breakToggle) return;

    const dur = rootEl.querySelector('#mdBreakDuration');
    const start = rootEl.querySelector('#mdBreakStart');
    const end = rootEl.querySelector('#mdBreakEnd');

    function syncMutualExclusion() {
        if (!dur || !start || !end) return;
        if (dur.value) {
            start.disabled = true;
            end.disabled = true;
            start.value = '';
            end.value = '';
            start.classList.add('opacity-50');
            end.classList.add('opacity-50');
        } else if (start.value || end.value) {
            dur.disabled = true;
            dur.value = '';
            dur.classList.add('opacity-50');
            start.disabled = false;
            end.disabled = false;
            start.classList.remove('opacity-50');
            end.classList.remove('opacity-50');
        } else {
            dur.disabled = false;
            start.disabled = false;
            end.disabled = false;
            dur.classList.remove('opacity-50');
            start.classList.remove('opacity-50');
            end.classList.remove('opacity-50');
        }
    }
    if (dur && start && end) {
        dur.addEventListener('input', syncMutualExclusion);
        start.addEventListener('change', syncMutualExclusion);
        end.addEventListener('change', syncMutualExclusion);
    }

    function toggleBreakForm() {
        breakForm.classList.toggle('hidden');
        breakToggle.classList.toggle('hidden');
        if (breakError) {
            breakError.classList.add('hidden');
            breakError.textContent = '';
        }
    }

    function addBreak() {
        const typeEl = rootEl.querySelector('#mdBreakType');
        const durationEl = dur;
        const startEl = start;
        const endEl = end;
        if (!typeEl || !durationEl || !startEl || !endEl) return;
        if (breakError) {
            breakError.classList.add('hidden');
            breakError.textContent = '';
        }

        const duration = durationEl.value.trim();
        const startTime = startEl.value.trim();
        const endTime = endEl.value.trim();

        if (!duration && !startTime) {
            if (breakError) {
                breakError.textContent = T.breakProvideTimesOrDuration || '';
                breakError.classList.remove('hidden');
            }
            return;
        }

        if (startTime && !endTime && !duration) {
            if (breakError) {
                breakError.textContent = T.breakProvideEndOrDuration || '';
                breakError.classList.remove('hidden');
            }
            return;
        }

        if (duration) {
            const d = parseInt(duration, 10);
            if (isNaN(d) || d < 1) {
                if (breakError) {
                    breakError.textContent = T.breakInvalidDuration || '';
                    breakError.classList.remove('hidden');
                }
                return;
            }
        }

        breaks.push({
            type: typeEl.value,
            typeLabel: typeEl.options[typeEl.selectedIndex].text,
            duration,
            start: startTime,
            end: endTime,
        });

        typeEl.value = 'rest';
        durationEl.value = '';
        durationEl.disabled = false;
        durationEl.classList.remove('opacity-50');
        startEl.value = '';
        startEl.disabled = false;
        startEl.classList.remove('opacity-50');
        endEl.value = '';
        endEl.disabled = false;
        endEl.classList.remove('opacity-50');
        breakForm.classList.add('hidden');
        breakToggle.classList.remove('hidden');
        renderBreaks();
    }

    function removeBreak(idx) {
        breaks.splice(idx, 1);
        renderBreaks();
    }

    function renderBreaks() {
        breaksList.innerHTML = '';
        breaksHidden.innerHTML = '';
        if (breaks.length === 0) {
            breaksList.style.display = 'none';
            return;
        }
        breaksList.style.display = 'block';

        const totalMin = breaks.reduce((acc, b) => acc + (parseInt(b.duration, 10) || 0), 0);

        const header = document.createElement('div');
        header.className = 'flex items-center justify-between mb-1.5';
        header.innerHTML =
            '<span class="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center">' +
                '<svg class="w-3 h-3 mr-1 text-orange-500" fill="currentColor" viewBox="0 0 20 20">' +
                    '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>' +
                '</svg>' +
                (T.breaksLabel || 'Breaks') +
            '</span>' +
            `<span class="text-[10px] text-gray-400 dark:text-gray-500">${totalMin} min</span>`;
        breaksList.appendChild(header);

        const rowsDiv = document.createElement('div');
        rowsDiv.className = 'space-y-1';
        breaks.forEach((b, i) => {
            const bc = BADGE_CLASSES[b.type] || BADGE_CLASSES.other;
            const timeRange = (b.start && b.end)
                ? `<span class="text-gray-400 dark:text-gray-500">${b.start} – ${b.end}</span>`
                : '';
            const durSpan = b.duration
                ? `<span class="font-medium text-gray-900 dark:text-white">${b.duration} min</span>`
                : '';

            const row = document.createElement('div');
            row.className = 'flex items-center justify-between py-1 px-2 rounded text-xs bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-600 group';
            row.innerHTML =
                '<div class="flex items-center gap-2">' +
                    `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${bc}">${b.typeLabel}</span>` +
                    durSpan + timeRange +
                '</div>' +
                '<div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">' +
                    `<button type="button" data-md-action="remove-break" data-md-break-idx="${i}" class="p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400">` +
                        '<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>' +
                    '</button>' +
                '</div>';
            rowsDiv.appendChild(row);

            ['type', 'duration', 'start', 'end'].forEach((key) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `break_${key}s`;
                input.value = b[key] || '';
                breaksHidden.appendChild(input);
            });
        });
        breaksList.appendChild(rowsDiv);
    }

    rootEl.addEventListener('click', (evt) => {
        const target = evt.target.closest('[data-md-action]');
        if (!target) return;
        const action = target.dataset.mdAction;
        if (action === 'toggle-break-form') {
            evt.preventDefault();
            toggleBreakForm();
        } else if (action === 'add-break') {
            evt.preventDefault();
            addBreak();
        } else if (action === 'remove-break') {
            evt.preventDefault();
            const idx = parseInt(target.dataset.mdBreakIdx, 10);
            if (!isNaN(idx)) removeBreak(idx);
        }
    });
}

if (typeof window !== 'undefined') {
    window.visitDrawerInits = window.visitDrawerInits || {};
    window.visitDrawerInits.visitMarkDone = initVisitMarkDoneDrawer;
}

export { initVisitMarkDoneDrawer };
